/**
 * The rules of the Live Promote sidebar, free of I/O: where each scenario stands (reply being written, written and
 * waiting, promoted as a draft, auto-sending), how its Ready and its writes read, and the settings a switch makes.
 */

import type { JobStatus, PromotionJob, PromotionPlan, ReplyMode, ScenarioSetting, WriteMode } from '@/modules/promotion/types';
import type { LiveReadiness, LiveType, LiveWrite } from '../types';

/**
 * `no_reply`: no reply yet. `waiting`: a reply is written and not added. `draft`: added, the template is off, so
 * Kim drafts the usual way. `auto`: added and sends by itself.
 */
export type ScenarioState = 'no_reply' | 'waiting' | 'draft' | 'auto';

export const isPromoted = (state: ScenarioState): boolean => state === 'draft' || state === 'auto';

export const scenarioStateOf = (type: Pick<LiveType, 'templates' | 'draft'>, setting?: ScenarioSetting): ScenarioState => {
	if (type.templates > 0) return setting?.reply === 'auto' ? 'auto' : 'draft';
	return type.draft ? 'waiting' : 'no_reply';
};

const ACTIVE_JOB: JobStatus[] = ['queued', 'running'];

/** Whether the latest generation is writing this scenario's reply right now. */
export const isWritingReply = (job: Pick<PromotionJob, 'status' | 'types'> | null | undefined, typeId: string): boolean =>
	Boolean(
		job &&
			ACTIVE_JOB.includes(job.status) &&
			job.types.some((type) => type.typeId === typeId && ACTIVE_JOB.includes(type.status))
	);

/** The state badge: a scenario with no reply says so, unless one is being written now. */
export const stateLabelOf = (state: ScenarioState, writingReply: boolean): string => {
	if (state === 'no_reply') return writingReply ? 'Writing its reply…' : 'No reply yet';
	return { waiting: 'Reply written', draft: 'Draft', auto: 'Auto-send' }[state];
};

/** The scenario's short name; a type written before names existed falls back to its sentence. */
export const scenarioLabelOf = (type: Pick<LiveType, 'name' | 'description'>): string => type.name?.trim() || type.description;

/** The settings in force: the active promotion's, else every reply a draft and every write asking. */
export const currentSettingsOf = (plan: Pick<PromotionPlan, 'types' | 'promotion'>): Record<string, ScenarioSetting> => {
	const active = plan.promotion?.status === 'active' ? plan.promotion.scenarios : {};
	return Object.fromEntries(
		plan.types.map((type): [string, ScenarioSetting] => {
			const setting = active[type.typeId];
			return [
				type.typeId,
				{
					reply: setting?.reply ?? 'draft',
					writes: Object.fromEntries(type.writes.map((write) => [write.tool, setting?.writes[write.tool] ?? 'ask']))
				}
			];
		})
	);
};

/** The settings with one scenario's reply changed. A reply going back to a draft takes its writes back to asking. */
export const withReply = (
	settings: Record<string, ScenarioSetting>,
	typeId: string,
	reply: ReplyMode
): Record<string, ScenarioSetting> => {
	const current = settings[typeId] ?? { reply: 'draft', writes: {} };
	const writes =
		reply === 'draft'
			? Object.fromEntries(Object.keys(current.writes).map((tool): [string, WriteMode] => [tool, 'ask']))
			: current.writes;
	return { ...settings, [typeId]: { reply, writes } };
};

export const withWrite = (
	settings: Record<string, ScenarioSetting>,
	typeId: string,
	tool: string,
	mode: WriteMode
): Record<string, ScenarioSetting> => {
	const current = settings[typeId] ?? { reply: 'draft', writes: {} };
	return { ...settings, [typeId]: { ...current, writes: { ...current.writes, [tool]: mode } } };
};

/** The scenario's Ready as a short badge: "Ready", "Not ready · 84%", "Too few tickets · 12 of 20". */
export const readyBadgeOf = (ready: LiveReadiness, minTickets: number | null): string => {
	if (ready.verdict === 'ready') return 'Ready';
	if (ready.verdict === 'not_ready') return `Not ready · ${ready.goodPercent ?? 0}%`;
	return minTickets ? `Too few tickets · ${ready.graded} of ${minTickets}` : 'Too few tickets';
};

/** "Seen 44 · 84% Perfect or Good Enough". */
export const seenLineOf = (type: Pick<LiveType, 'tickets' | 'ready'>): string =>
	type.ready.goodPercent === null
		? `Seen ${type.tickets} · not graded yet`
		: `Seen ${type.tickets} · ${type.ready.goodPercent}% Perfect or Good Enough`;

/** A rate as a whole percent; null when nothing was decided. */
export const percentOf = (rate: number | null): string | null => (rate === null ? null : `${Math.round(rate * 100)}%`);

/** "3 of 10 approvals in a row" or "Ready · 12 of 10". */
export const runLineOf = (write: Pick<LiveWrite, 'streak' | 'required' | 'ready'>): string =>
	write.ready
		? `${write.streak} approved in a row (${write.required} needed)`
		: `${write.streak} of ${write.required} approvals in a row`;

/** The protocol's summary line: "3 of 6 on · 1 sends by itself · 1 write runs without approval". */
export const promotedSummaryOf = (states: ScenarioState[], autoWrites: number): string => {
	const on = states.filter(isPromoted).length;
	const auto = states.filter((state) => state === 'auto').length;
	return [
		`${on} of ${states.length} on`,
		`${auto} ${auto === 1 ? 'sends' : 'send'} by itself`,
		`${autoWrites} ${autoWrites === 1 ? 'write runs' : 'writes run'} without approval`
	].join(' · ');
};

const DAY = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short' });
const TIME = new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });

/** A time as the sidebar shows it, in the viewer's zone: "31 Oct", or "31 Oct, 14:20" with the time. */
export const formatWhen = (iso: string, withTime = false): string => {
	const at = new Date(iso);
	if (Number.isNaN(at.getTime())) return '';
	return withTime ? `${DAY.format(at)}, ${TIME.format(at)}` : DAY.format(at);
};
